import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Document } from '../types'
import { analyzeDocument, getDocumentAnalysis, getSpecificAnalysis, getDocumentStatus } from '../utils/api'
import { PDFViewer } from './PDFViewer'
import SmartDataRenderer from './SmartDataRenderer'
import {
  ChevronLeftIcon,
  RefreshCwIcon,
  FileTextIcon,
  AlertCircleIcon,
  ChevronRightIcon,
  BrainCircuitIcon,
  SparklesIcon,
  ChevronDownIcon,
  ClockIcon,
  HistoryIcon,
  CodeIcon,
  XIcon,
} from 'lucide-react'

interface SmartAnalysisViewProps {
  document: Document
  onBackToList: () => void
}

export const SmartAnalysisView = ({ document, onBackToList }: SmartAnalysisViewProps) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isLoadingExisting, setIsLoadingExisting] = useState(true)
  const [isCheckingAutoAnalysis, setIsCheckingAutoAnalysis] = useState(true)
  const [currentDocument, setCurrentDocument] = useState<Document>(document)
  const [error, setError] = useState<string | null>(null)
  const [isPdfOpen, setIsPdfOpen] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [analysisData, setAnalysisData] = useState<any>(null)
  const [analysisHistory, setAnalysisHistory] = useState<any[]>([])
  const [currentAnalysisInfo, setCurrentAnalysisInfo] = useState<any>(null)
  const [isHistoryDropdownOpen, setIsHistoryDropdownOpen] = useState(false)
  const [isRawJsonModalOpen, setIsRawJsonModalOpen] = useState(false)
  const [fullAnalysisRecord, setFullAnalysisRecord] = useState<any>(null)
  
  // Pane width management with localStorage persistence
  const [analysisWidth, setAnalysisWidth] = useState(() => {
    const savedWidth = localStorage.getItem('iep-scout-analysis-width')
    return savedWidth ? parseFloat(savedWidth) : 40 // Default to 40% for analysis pane
  })

  // Define callback functions before useEffects that depend on them
  const loadExistingAnalysis = useCallback(async () => {
    try {
      setIsLoadingExisting(true)
      const response = await getDocumentAnalysis(document.id)
      console.log('Analysis response:', response)
      
      // Check different possible response structures
      let analysisData = null
      
      if ((response as any)?.analysis) {
        // Standard API response: { success: true, analysis: {...} }
        analysisData = (response as any).analysis
      } else if (response?.student_info || response?.goals || response?.services) {
        // Direct analysis data (if API returns analysis object directly)
        analysisData = response
      } else if (response && typeof response === 'object') {
        // Check if response has analysis-like structure
        const hasAnalysisFields = ['student_info', 'goals', 'services', 'accommodations', 'disabilities', 'present_levels']
        const hasFields = hasAnalysisFields.some(field => (response as any)[field])
        if (hasFields) {
          analysisData = response
        }
      }
      
      if (analysisData) {
        setAnalysisData(analysisData)
        setFullAnalysisRecord(response) // Store the full response for debugging
        console.log('Loaded existing analysis:', analysisData)
        
        // Set analysis history and current info if available
        if ((response as any).analysis_history) {
          setAnalysisHistory((response as any).analysis_history)
        }
        if ((response as any).current_analysis) {
          setCurrentAnalysisInfo((response as any).current_analysis)
        }
      } else {
        console.log('No valid analysis structure found in response')
      }
    } catch (err) {
      // No existing analysis found - that's ok
      console.log('No existing analysis found:', err)
    } finally {
      setIsLoadingExisting(false)
    }
  }, [document.id])

  const handleAnalyze = useCallback(async () => {
    setIsAnalyzing(true)
    setError(null)
    try {
      const response = await analyzeDocument(document.id)
      console.log('Analyze response:', response)
      
      // Check different possible response structures
      let analysisData = null
      
      if ((response as any)?.analysis) {
        // Standard API response: { success: true, analysis: {...} }
        analysisData = (response as any).analysis
      } else if (response?.student_info || response?.goals || response?.services) {
        // Direct analysis data
        analysisData = response
      } else if (response && typeof response === 'object') {
        // Check if response has analysis-like structure
        const hasAnalysisFields = ['student_info', 'goals', 'services', 'accommodations', 'disabilities', 'present_levels']
        const hasFields = hasAnalysisFields.some(field => (response as any)[field])
        if (hasFields) {
          analysisData = response
        }
      }
      
      if (analysisData) {
        setAnalysisData(analysisData)
        setFullAnalysisRecord(response) // Store the full response for debugging
        // Update document status
        document.processing_status = 'completed'
      } else {
        setError('Analysis completed but no valid data structure returned.')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to analyze document. Please try again.')
    } finally {
      setIsAnalyzing(false)
    }
  }, [document.id])

  useEffect(() => {
    // Always try to load existing analysis first
    loadExistingAnalysis()
  }, [document.id, loadExistingAnalysis])

  // Auto-trigger analysis for newly uploaded documents
  useEffect(() => {
    const checkAndAutoAnalyze = async () => {
      // Check if this is a newly uploaded document that needs analysis
      const needsAnalysis = (
        document.processing_status === 'uploaded' &&
        !analysisData && 
        !isAnalyzing && 
        !isLoadingExisting
      )
      
      if (needsAnalysis) {
        console.log('🚀 Auto-triggering analysis for newly uploaded document')
        await handleAnalyze()
      }
      
      // We've made the decision about auto-analysis
      setIsCheckingAutoAnalysis(false)
    }

    // Wait a bit for existing analysis loading to complete
    const timer = setTimeout(checkAndAutoAnalyze, 1000)
    return () => clearTimeout(timer)
  }, [document.processing_status, analysisData, isAnalyzing, isLoadingExisting, handleAnalyze])

  // Poll for analysis completion if we're analyzing
  useEffect(() => {
    let pollInterval: NodeJS.Timeout

    if (isAnalyzing) {
      console.log('📡 Starting to poll for analysis completion')
      pollInterval = setInterval(async () => {
        try {
          const updatedDoc = await getDocumentStatus(document.id)
          setCurrentDocument(updatedDoc)
          
          if (updatedDoc.processing_status === 'completed') {
            console.log('✅ Analysis completed! Loading results...')
            setIsAnalyzing(false)
            // Reload the analysis data
            await loadExistingAnalysis()
          } else if (updatedDoc.processing_status === 'error') {
            console.log('❌ Analysis failed')
            setIsAnalyzing(false)
            setError('Analysis failed. Please try again.')
          }
        } catch (error) {
          console.error('Error polling document status:', error)
        }
      }, 3000) // Poll every 3 seconds
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [isAnalyzing, document.id, loadExistingAnalysis])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isHistoryDropdownOpen) {
        const dropdown = window.document.querySelector('.analysis-history-dropdown')
        if (dropdown && !dropdown.contains(event.target as Node)) {
          setIsHistoryDropdownOpen(false)
        }
      }
    }

    window.document.addEventListener('mousedown', handleClickOutside)
    return () => {
      window.document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isHistoryDropdownOpen])


  const loadSpecificAnalysis = async (iepId: string) => {
    try {
      setIsLoadingExisting(true)
      const response = await getSpecificAnalysis(document.id, iepId)
      console.log('Specific analysis response:', response)
      
      if ((response as any).analysis) {
        setAnalysisData((response as any).analysis)
        setFullAnalysisRecord(response) // Store the full response for debugging
        setCurrentAnalysisInfo((response as any).current_analysis)
        setIsHistoryDropdownOpen(false)
      }
    } catch (err) {
      console.error('Failed to load specific analysis:', err)
      setError('Failed to load selected analysis')
    } finally {
      setIsLoadingExisting(false)
    }
  }

  const handleSourceClick = (pageNumber: number) => {
    console.log('Source click:', pageNumber, 'PDF open:', isPdfOpen)
    
    if (!isPdfOpen) {
      setIsPdfOpen(true)
      // Set the page first, then add a delay to ensure scrolling happens after PDF loads
      setCurrentPage(pageNumber)
    } else {
      // PDF is already open, just change the page
      setCurrentPage(pageNumber)
    }
  }


  return (
    <div className="w-full h-full relative">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={onBackToList}
            className="mr-4 p-1 rounded-full hover:bg-gray-200"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex items-center space-x-3">
            <h1 className="text-2xl font-bold text-gray-900 truncate max-w-xl">
              {document.original_filename}
            </h1>
            {/* Student name prominently displayed next to filename */}
            {currentDocument.students && currentDocument.students.length > 0 ? (
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400">•</span>
                {currentDocument.students.map((student, index) => (
                  <Link
                    key={student.id}
                    to={`/student/${student.id}`}
                    className="text-lg font-semibold text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {student.first_name} {student.last_name}
                    {index < currentDocument.students!.length - 1 && ", "}
                  </Link>
                ))}
              </div>
            ) : currentDocument.student_name_detected ? (
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400">•</span>
                <span className="text-lg font-semibold text-gray-700">
                  {currentDocument.student_name_detected}
                </span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Analysis History Dropdown */}
          {analysisHistory.length > 0 && (
            <div className="relative analysis-history-dropdown">
              <button
                onClick={() => setIsHistoryDropdownOpen(!isHistoryDropdownOpen)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <HistoryIcon className="h-4 w-4 mr-2" />
                {currentAnalysisInfo ? `Version ${currentAnalysisInfo.version}` : 'Select Analysis'}
                <ChevronDownIcon className="h-4 w-4 ml-1" />
              </button>
              
              {isHistoryDropdownOpen && (
                <div className="absolute right-0 z-10 mt-2 w-80 bg-white border border-gray-200 rounded-md shadow-lg">
                  <div className="py-1">
                    <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase border-b">
                      Analysis History ({analysisHistory.length} versions)
                    </div>
                    {analysisHistory.map((analysis) => (
                      <button
                        key={analysis.iep_id}
                        onClick={() => loadSpecificAnalysis(analysis.iep_id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center justify-between ${
                          currentAnalysisInfo?.iep_id === analysis.iep_id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                        }`}
                      >
                        <div>
                          <div className="flex items-center">
                            <span className="font-medium">Version {analysis.version}</span>
                            {analysis.is_latest && (
                              <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                                Latest
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            <ClockIcon className="h-3 w-3 inline mr-1" />
                            {new Date(analysis.extracted_at).toLocaleString()}
                          </div>
                        </div>
                        {currentAnalysisInfo?.iep_id === analysis.iep_id && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                      </button>
                    ))}
                    <div className="border-t mt-1">
                      <button
                        onClick={handleAnalyze}
                        disabled={isAnalyzing}
                        className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center disabled:opacity-50"
                      >
                        <RefreshCwIcon className={`h-4 w-4 mr-2 ${isAnalyzing ? 'animate-spin' : ''}`} />
                        {isAnalyzing ? 'Generating new analysis...' : 'Generate new analysis'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Debug: View Raw JSON Button */}
          {fullAnalysisRecord && (
            <button
              onClick={() => setIsRawJsonModalOpen(true)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              title="View Raw JSON (Debug)"
            >
              <CodeIcon className="h-4 w-4 mr-2" />
              View Raw JSON
            </button>
          )}

          {!analysisData && analysisHistory.length === 0 && (
            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCwIcon
                className={`h-4 w-4 mr-1 ${isAnalyzing ? 'animate-spin' : ''}`}
              />
              {isAnalyzing ? 'Analyzing...' : 'Analyze Document'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
          <AlertCircleIcon className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex h-[calc(100vh-220px)] relative">
        <div
          style={{ width: isPdfOpen ? `${analysisWidth}%` : '100%' }}
          className={`transition-all duration-300 ease-in-out`}
        >
          {isLoadingExisting ? (
            <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg border border-gray-200 p-8">
              <div className="relative">
                <RefreshCwIcon className="h-12 w-12 text-blue-500 animate-spin" />
                <SparklesIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                Loading analysis...
              </h3>
              <p className="text-gray-500 text-center">
                Checking for existing analysis results.
              </p>
            </div>
          ) : analysisData ? (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col">
              <div className="flex-1 overflow-auto p-6">
                <SmartDataRenderer
                  data={analysisData}
                  fieldName="analysis"
                  onSourceClick={handleSourceClick}
                  maxLevel={4}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg border border-gray-200 p-8">
              {isAnalyzing ? (
                <>
                  <div className="relative">
                    <RefreshCwIcon className="h-12 w-12 text-blue-500 animate-spin" />
                    <SparklesIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                    Analyzing document...
                  </h3>
                  <p className="text-gray-500 text-center">
                    Our AI is extracting and organizing information from your
                    IEP document. This may take a few moments.
                  </p>
                </>
              ) : isCheckingAutoAnalysis ? (
                <>
                  <div className="relative">
                    <RefreshCwIcon className="h-12 w-12 text-gray-500 animate-spin" />
                    <BrainCircuitIcon className="h-6 w-6 text-blue-500 absolute -top-1 -right-1 animate-pulse" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                    Checking document status...
                  </h3>
                  <p className="text-gray-500 text-center">
                    Determining if analysis is needed for this document.
                  </p>
                </>
              ) : (
                <>
                  <FileTextIcon className="h-12 w-12 text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Document not analyzed
                  </h3>
                  <p className="text-gray-500 text-center mb-4">
                    Click the "Analyze Document" button to extract structured
                    information from this IEP.
                  </p>
                  <button
                    onClick={handleAnalyze}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    <RefreshCwIcon className="h-4 w-4 mr-1" />
                    Analyze Document
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Resize Handle */}
        {isPdfOpen && (
          <div
            className="relative flex-shrink-0 w-1 bg-gray-200 hover:bg-gray-300 cursor-col-resize transition-colors duration-150"
            onMouseDown={(e) => {
              e.preventDefault()
              const startX = e.clientX
              const startWidth = analysisWidth
              const containerRect = e.currentTarget.parentElement?.getBoundingClientRect()
              if (!containerRect) return

              const handleMouseMove = (moveEvent: MouseEvent) => {
                const deltaX = moveEvent.clientX - startX
                const containerWidth = containerRect.width - 4 // account for resize handle width
                const deltaPercent = (deltaX / containerWidth) * 100
                const newWidth = Math.max(25, Math.min(75, startWidth + deltaPercent))
                setAnalysisWidth(newWidth)
              }

              const handleMouseUp = () => {
                window.document.removeEventListener('mousemove', handleMouseMove)
                window.document.removeEventListener('mouseup', handleMouseUp)
                window.document.body.style.userSelect = ''
                window.document.body.style.cursor = ''
                localStorage.setItem('iep-scout-analysis-width', analysisWidth.toString())
              }

              window.document.addEventListener('mousemove', handleMouseMove)
              window.document.addEventListener('mouseup', handleMouseUp)
              window.document.body.style.userSelect = 'none'
              window.document.body.style.cursor = 'col-resize'
            }}
          >
            {/* Visual indicator for the handle */}
            <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 w-3 flex items-center justify-center">
              <div className="w-0.5 h-8 bg-gray-400 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-150"></div>
            </div>
            
            {/* Invisible wider drag area for easier grabbing */}
            <div className="absolute inset-y-0 -left-2 -right-2 cursor-col-resize"></div>
          </div>
        )}

        <div
          style={{ width: isPdfOpen ? `${100 - analysisWidth}%` : '0%' }}
          className={`transition-all duration-300 ease-in-out overflow-hidden relative ${
            isPdfOpen ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {isPdfOpen && (
            <>
              <button
                onClick={() => setIsPdfOpen(false)}
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-r-md p-1 shadow-md"
                title="Hide PDF"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div className="pl-4 w-full h-full">
                <PDFViewer
                  documentId={document.id}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </>
          )}
        </div>

        {!isPdfOpen && (
          <button
            onClick={() => setIsPdfOpen(true)}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-l-md p-1 shadow-md"
            title="Show PDF"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Keep the non-PDF layout as is */}
      {false && (
        <div className="h-[calc(100vh-220px)] relative">
          <div className="w-full h-full">
            {isLoadingExisting ? (
              <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg border border-gray-200 p-8">
                <div className="relative">
                  <RefreshCwIcon className="h-12 w-12 text-blue-500 animate-spin" />
                  <SparklesIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1 animate-pulse" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                  Loading analysis...
                </h3>
                <p className="text-gray-500 text-center">
                  Checking for existing analysis results.
                </p>
              </div>
            ) : analysisData ? (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden h-full flex flex-col">
                <div className="flex-1 overflow-auto p-6">
                  <SmartDataRenderer
                    data={analysisData}
                    fieldName="analysis"
                    onSourceClick={handleSourceClick}
                    maxLevel={4}
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full bg-white rounded-lg border border-gray-200 p-8">
                {isAnalyzing ? (
                  <>
                    <div className="relative">
                      <RefreshCwIcon className="h-12 w-12 text-blue-500 animate-spin" />
                      <SparklesIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1 animate-pulse" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                      Analyzing document...
                    </h3>
                    <p className="text-gray-500 text-center">
                      Our AI is extracting and organizing information from your
                      IEP document. This may take a few moments.
                    </p>
                  </>
                ) : isCheckingAutoAnalysis ? (
                  <>
                    <div className="relative">
                      <RefreshCwIcon className="h-12 w-12 text-gray-500 animate-spin" />
                      <BrainCircuitIcon className="h-6 w-6 text-blue-500 absolute -top-1 -right-1 animate-pulse" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">
                      Checking document status...
                    </h3>
                    <p className="text-gray-500 text-center">
                      Determining if analysis is needed for this document.
                    </p>
                  </>
                ) : (
                  <>
                    <FileTextIcon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Document not analyzed
                    </h3>
                    <p className="text-gray-500 text-center mb-4">
                      Click the "Analyze Document" button to extract structured
                      information from this IEP.
                    </p>
                    <button
                      onClick={handleAnalyze}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <RefreshCwIcon className="h-4 w-4 mr-1" />
                      Analyze Document
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          
          <button
            onClick={() => setIsPdfOpen(true)}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white border border-gray-200 rounded-l-md p-1 shadow-md"
            title="Show PDF"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      )}

      {/* Raw JSON Debug Modal */}
      {isRawJsonModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">Raw Analysis Record (Debug)</h3>
              <button
                onClick={() => setIsRawJsonModalOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200"
              >
                <XIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <pre className="bg-gray-50 p-4 rounded-md text-xs font-mono whitespace-pre-wrap overflow-auto">
                {JSON.stringify(fullAnalysisRecord, null, 2)}
              </pre>
            </div>
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(fullAnalysisRecord, null, 2))
                    .then(() => alert('JSON copied to clipboard!'))
                    .catch(() => alert('Failed to copy to clipboard'))
                }}
                className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Copy JSON
              </button>
              <button
                onClick={() => setIsRawJsonModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}